import React, { CSSProperties, useMemo } from 'react';
import { themeColours } from 'fym-shared/src/music24/releaseThemes';
import { canvasCoverBox, canvasDeco, canvasGround, canvasShade } from 'fym-shared/src/music24/story/canvasVideo';
import { storyCoverFrame, storyRecord, type StoryColours } from 'fym-shared/src/music24/story/storyStyles';
import { canvasWatermark, watermarkTone } from 'fym-shared/src/music24/story/watermark';
import { TypedMemo } from '../../../../common/types';
import type { StoryPreviewContext } from '../details/imageTiles/storyPreviewContext';
import styles from './StoryPreview.module.scss';
import { WatermarkLockup } from './WatermarkLockup';

const FULL: CSSProperties = { position: 'absolute', inset: 0 };
/** The Hype sticker and the Handmade photo rest at their theme tilt between two kicks. */
const REST_TILT: Readonly<Record<string, number>> = { hype: 5, handmade: -4 };

interface CanvasVideoPreviewProps {
  context: StoryPreviewContext;
  width: number;
}

/**
 * The step-3 preview of the Canvas loop (design `asset-redesign/s`): one frame between two kicks,
 * drawn with the tables of the render (fym-shared `canvasVideo.ts`): the whole cover in its theme
 * frame in the free window, the still ornaments, the shade over the top bar and the player zone,
 * and on Free the row lockup of design w0 under the cover stage (`canvasWatermark`).
 */
const CanvasVideoPreviewComponent: React.FC<CanvasVideoPreviewProps> = ({ context, width }) => {
  const { themeId } = context.look;
  const colors = useMemo<StoryColours>(() => {
    const colours = themeColours(themeId, context.palette);
    return {
      ink: colours.background,
      surface: colours.surface,
      text: colours.text,
      accent: colours.accent,
      onAccent: colours.onAccent,
    };
  }, [context.palette, themeId]);
  const box = canvasCoverBox(themeId);
  const ground = canvasGround(themeId, colors);
  const frame = storyCoverFrame(themeId, false, colors, 1);
  const scale = width / 1080;

  return (
    <div className={styles.frame} style={{ width, height: 1920 * scale }} data-testid="canvas-preview">
      <div
        className={styles.canvas}
        style={{ width: 1080, height: 1920, transform: `scale(${scale})`, background: colors.ink }}
      >
        {themeId === 'clean' && context.artworkUrl !== undefined && (
          <img
            src={context.artworkUrl}
            alt=""
            style={{
              position: 'absolute',
              left: '-20%',
              top: '-10%',
              width: '140%',
              height: '120%',
              objectFit: 'cover',
              filter: 'blur(70px) saturate(1.1)',
              opacity: 0.5,
            }}
          />
        )}
        {ground !== null && <div style={{ ...FULL, background: ground }} />}
        {canvasDeco(themeId, colors).map((deco) => (
          <div key={JSON.stringify(deco)} style={deco} />
        ))}
        <div
          style={{
            position: 'absolute',
            left: box.x,
            top: box.y,
            width: box.w,
            boxSizing: 'border-box',
            ...frame.outer,
            transform: `rotate(${REST_TILT[themeId] ?? 0}deg)`,
          }}
        >
          {themeId === 'retro' && <div style={storyRecord(colors, 1)} />}
          {context.artworkUrl === undefined ? (
            <div className={styles.picture} style={{ background: colors.accent, ...frame.picture }} />
          ) : (
            <img className={styles.picture} src={context.artworkUrl} alt="" style={frame.picture} />
          )}
          {frame.tape !== null && <div style={frame.tape} />}
        </div>
        <div style={{ ...FULL, background: canvasShade(colors) }} />
        {context.watermark && (
          <WatermarkLockup
            testId="canvas-preview-watermark"
            lockup={canvasWatermark().lockup}
            tone={watermarkTone(colors.ink, false)}
            x={canvasWatermark().x}
            y={canvasWatermark().y}
          />
        )}
      </div>
    </div>
  );
};

export const CanvasVideoPreview = TypedMemo(CanvasVideoPreviewComponent);
